// src/rms/utils/datetime.js
// ============================================================================
// When a form has to guess a time
// ============================================================================
// Every screen that opens a blank booking, block or delivery has to put
// something in the datetime field. It used to put `now`, which produced
// 12:37 PM -- a time nobody chose, precise to the minute, and wrong the moment
// anybody looked at it.
//
// Noon instead, for three reasons.
//
// It is obviously a placeholder. 12:00 reads as "that day, sort out the time
// later"; 12:37 reads as a decision somebody made, and staff leave decisions
// alone.
//
// It survives daylight saving. Midnight does not exist on the spring-forward
// date in several zones -- Chile, Lebanon, Cuba, and until recently Brazil --
// so a date defaulted to 00:00 there is a timestamp that never happened.
// Midday exists on every date in every zone there has ever been.
//
// It is the middle of a rental day. A pickup defaulted to noon and a return
// defaulted to noon is a whole number of days, which is what the rate engine
// charges for and what a customer is told.
// ============================================================================

/** A copy of `date` at 12:00:00.000 local. Never mutates its argument. */
export function noonOn(date) {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  return d;
}

/** Noon, `days` from today. Negative goes backwards. */
export function noonInDays(days = 0) {
  const d = noonOn(new Date());
  // setDate rather than adding milliseconds: a day is not always 864e5 ms, and
  // adding a fixed amount across a DST boundary lands an hour either side of
  // noon -- which is exactly the precision this file exists to avoid.
  d.setDate(d.getDate() + days);
  return d;
}

/**
 * `yyyy-MM-ddTHH:mm`, which is the only format datetime-local accepts.
 *
 * Local, never toISOString -- that converts to UTC and would show a Vancouver
 * operator a pickup seven hours after the one they set.
 */
export function toLocalInput(date) {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** The two together, which is what every call site actually wants. */
export const noonInputInDays = (days = 0) => toLocalInput(noonInDays(days));

export default { noonOn, noonInDays, toLocalInput, noonInputInDays };
